'use client';
/* eslint-disable @typescript-eslint/no-explicit-any */

import { useCallback, useEffect, useRef, useState, useTransition } from 'react';
import { useRouter } from 'next/navigation';
import { AnimatePresence, motion } from 'framer-motion';
import {
  Banknote,
  Briefcase,
  Building2,
  ChevronDown,
  Home,
  Landmark,
  LandPlot,
  Loader2,
  MapPin,
  Maximize2,
  Search,
  Store,
  X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn, formatPrice, PROPERTY_TYPE_GROUPS, PROPERTY_TYPE_LABEL_MAP } from '@/lib/utils';
import { EASE_PREMIUM } from '@/lib/motion';

const BROWSER_API = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3000';

const PROP_TYPE_ICONS: Record<string, React.ReactNode> = {
  apartment: <Building2 className="h-4 w-4" />,
  studio: <Building2 className="h-4 w-4" />,
  villa: <Home className="h-4 w-4" />,
  maison: <Home className="h-4 w-4" />,
  riad: <Landmark className="h-4 w-4" />,
  terrain_urbain: <LandPlot className="h-4 w-4" />,
  terrain_rural: <LandPlot className="h-4 w-4" />,
  terrain_agricole: <LandPlot className="h-4 w-4" />,
  commercial: <Store className="h-4 w-4" />,
  bureau: <Briefcase className="h-4 w-4" />,
};

function fmtNum(v: string): string {
  const n = Number(v);
  if (!v || isNaN(n) || n === 0) return v;
  if (n >= 1_000_000) return `${+(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${Math.round(n / 1_000)}K`;
  return String(n);
}

type ActiveField = 'location' | 'type' | 'surface' | 'budget' | null;

interface StickyFilterBarProps {
  transactionType: 'sale' | 'rent';
  basePath: string;
  filterValues: {
    q?: string;
    type?: string;
    citySlug?: string;
    minPrice?: string;
    maxPrice?: string;
    minSurface?: string;
    maxSurface?: string;
    rooms?: string;
    bedrooms?: string;
    sort?: string;
    order?: string;
    isFeatured?: string;
  };
}

export function StickyFilterBar({ transactionType, basePath, filterValues }: StickyFilterBarProps) {
  const router = useRouter();
  const [isPending, startTransition] = useTransition();
  const barRef = useRef<HTMLDivElement>(null);
  const locationInputRef = useRef<HTMLInputElement>(null);

  const [activeField, setActiveField] = useState<ActiveField>(null);
  const [q, setQ] = useState(filterValues.q || '');
  const [type, setType] = useState(filterValues.type || '');
  const [minSurface, setMinSurface] = useState(filterValues.minSurface || '');
  const [maxSurface, setMaxSurface] = useState(filterValues.maxSurface || '');
  const [minPrice, setMinPrice] = useState(filterValues.minPrice || '');
  const [maxPrice, setMaxPrice] = useState(filterValues.maxPrice || '');

  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [cities, setCities] = useState<any[]>([]);
  const [focusedIdx, setFocusedIdx] = useState(-1);
  const [loadingSugg, setLoadingSugg] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    const h = (e: MouseEvent) => {
      if (barRef.current && !barRef.current.contains(e.target as Node)) {
        setActiveField(null);
      }
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const fetchSugg = useCallback(
    async (query: string) => {
      if (query.length < 1) { setSuggestions([]); setCities([]); setFocusedIdx(-1); return; }
      setLoadingSugg(true);
      try {
        const [propResp, cityResp] = await Promise.all([
          fetch(`${BROWSER_API}/api/properties?status=active&q=${encodeURIComponent(query)}&transactionType=${transactionType}&limit=4`, { cache: 'no-store' }),
          fetch(`${BROWSER_API}/api/cities/search?q=${encodeURIComponent(query)}`, { cache: 'no-store' }),
        ]);
        if (propResp.ok) { const d = await propResp.json(); setSuggestions(d.data || []); }
        if (cityResp.ok) { const d = await cityResp.json(); setCities(d.slice(0, 5)); }
      } catch { /* ignore */ } finally { setLoadingSugg(false); }
    },
    [transactionType],
  );

  useEffect(() => {
    if (q.length < 1) { setSuggestions([]); setCities([]); setFocusedIdx(-1); return; }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSugg(q), 150);
    return () => clearTimeout(debounceRef.current);
  }, [q, fetchSugg]);

  const applyFilters = () => {
    const params = new URLSearchParams();
    if (q.trim()) params.set('q', q.trim());
    if (type) params.set('type', type);
    if (minPrice) params.set('minPrice', minPrice);
    if (maxPrice) params.set('maxPrice', maxPrice);
    if (minSurface) params.set('minSurface', minSurface);
    if (maxSurface) params.set('maxSurface', maxSurface);
    if (filterValues.citySlug) params.set('citySlug', filterValues.citySlug);
    if (filterValues.rooms) params.set('rooms', filterValues.rooms);
    if (filterValues.bedrooms) params.set('bedrooms', filterValues.bedrooms);
    if (filterValues.isFeatured) params.set('isFeatured', filterValues.isFeatured);
    if (filterValues.sort) params.set('sort', filterValues.sort);
    if (filterValues.order) params.set('order', filterValues.order);
    const qs = params.toString();
    setActiveField(null);
    startTransition(() => router.push(`${basePath}${qs ? '?' + qs : ''}`));
  };

  const allSuggItems = [
    ...cities.map((c: any) => ({ kind: 'city' as const, data: c })),
    ...suggestions.map((p: any) => ({ kind: 'property' as const, data: p })),
  ];

  const handleLocationKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault(); setFocusedIdx((prev) => Math.min(prev + 1, allSuggItems.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault(); setFocusedIdx((prev) => Math.max(prev - 1, -1));
    } else if (e.key === 'Enter') {
      if (focusedIdx >= 0 && focusedIdx < allSuggItems.length) {
        const item = allSuggItems[focusedIdx];
        if (item.kind === 'city') { setQ(item.data.name); setActiveField(null); setFocusedIdx(-1); }
        else { setActiveField(null); startTransition(() => router.push(`/annonce/${item.data.slug}`)); }
      } else { applyFilters(); }
    } else if (e.key === 'Escape') { setActiveField(null); setFocusedIdx(-1); }
  };

  const typeLabel = type ? (PROPERTY_TYPE_LABEL_MAP[type] || type) : 'Type de bien';
  const surfaceLabel =
    minSurface && maxSurface ? `${minSurface} – ${maxSurface} m²`
      : minSurface ? `≥ ${minSurface} m²`
        : maxSurface ? `≤ ${maxSurface} m²`
          : 'Superficie';
  const budgetLabel =
    minPrice && maxPrice ? `${fmtNum(minPrice)} – ${fmtNum(maxPrice)} MAD`
      : minPrice ? `≥ ${fmtNum(minPrice)} MAD`
        : maxPrice ? `≤ ${fmtNum(maxPrice)} MAD`
          : transactionType === 'rent' ? 'Loyer' : 'Budget';

  const groups = PROPERTY_TYPE_GROUPS.filter(
    (g) => transactionType === 'sale' || g.available_for.includes('rent'),
  );

  const isOpen = (f: ActiveField) => activeField === f;

  return (
    <div
      ref={barRef}
      className="sticky top-16 z-40 bg-surface/95 backdrop-blur-md border-b border-border-subtle shadow-sm"
    >
      <div className="container-page">
        <div className="flex flex-wrap items-center gap-x-1.5 gap-y-2 py-2.5">

          {/* ── Location with autocomplete ── */}
          <div className="relative w-72">
            <div
              className={cn(
                'flex items-center gap-2 h-10 px-3 rounded-lg border bg-surface text-sm transition-colors cursor-text',
                isOpen('location') || q
                  ? 'border-cta ring-2 ring-cta/20'
                  : 'border-border hover:border-ink-secondary',
              )}
              onClick={() => { setActiveField('location'); locationInputRef.current?.focus(); }}
            >
              <MapPin className={cn('h-4 w-4 shrink-0', isOpen('location') || q ? 'text-accent' : 'text-ink-muted')} />
              <input
                ref={locationInputRef}
                type="text"
                value={q}
                onChange={(e) => { setQ(e.target.value); setActiveField('location'); }}
                onFocus={() => setActiveField('location')}
                onKeyDown={handleLocationKeyDown}
                placeholder="Ville, quartier…"
                className="flex-1 min-w-0 bg-transparent outline-none text-ink placeholder:text-ink-muted text-sm"
                aria-label="Localisation"
                aria-autocomplete="list"
              />
              {loadingSugg && <Loader2 className="h-3.5 w-3.5 text-ink-muted animate-spin shrink-0" />}
              {q && !loadingSugg && (
                <button
                  type="button"
                  onMouseDown={(e) => { e.preventDefault(); setQ(''); setSuggestions([]); setCities([]); }}
                  className="p-0.5 rounded-full hover:bg-surface-cream text-ink-muted hover:text-ink transition-colors shrink-0"
                  aria-label="Effacer"
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </div>

            <AnimatePresence>
              {isOpen('location') && q.length >= 1 && (
                <motion.div
                  initial={{ opacity: 0, y: -4 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -4 }}
                  transition={{ duration: 0.15, ease: EASE_PREMIUM }}
                  className="absolute left-0 top-full mt-1.5 w-[360px] bg-surface rounded-xl shadow-card-hover border border-border-subtle overflow-hidden z-50"
                  role="listbox"
                >
                  {loadingSugg && cities.length === 0 && suggestions.length === 0 && (
                    <div className="px-4 py-3 space-y-2.5">
                      {[1, 2, 3].map((n) => (
                        <div key={n} className="flex items-center gap-3 animate-pulse">
                          <div className="w-8 h-8 rounded-lg bg-surface-cream shrink-0" />
                          <div className="flex-1 space-y-1.5">
                            <div className="h-3 bg-surface-cream rounded w-2/3" />
                            <div className="h-2.5 bg-surface-cream rounded w-1/3" />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {cities.length > 0 && (
                    <div>
                      <p className="px-4 pt-3 pb-1 eyebrow text-[10px] flex items-center gap-1.5">
                        <MapPin className="h-3 w-3" /> Villes &amp; régions
                      </p>
                      {cities.map((city: any, i: number) => {
                        const focused = focusedIdx === i;
                        return (
                          <button
                            key={city.id}
                            type="button"
                            onMouseDown={() => { setQ(city.name); setActiveField(null); setFocusedIdx(-1); }}
                            onMouseEnter={() => setFocusedIdx(i)}
                            role="option"
                            aria-selected={focused}
                            className={cn(
                              'w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors',
                              focused ? 'bg-accent/[0.08]' : 'hover:bg-surface-cream',
                            )}
                          >
                            <div className={cn('w-8 h-8 rounded-lg flex items-center justify-center shrink-0 transition-colors', focused ? 'bg-accent/20 text-accent' : 'bg-surface-cream text-ink-muted')}>
                              <MapPin className="h-3.5 w-3.5" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-ink"><Highlight text={city.name} query={q} /></p>
                            </div>
                            {city.region?.name && (
                              <span className="text-[10px] text-ink-muted bg-surface-cream px-2 py-0.5 rounded-full shrink-0">{city.region.name}</span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  )}

                  {suggestions.length > 0 && (
                    <div className={cn(cities.length > 0 && 'border-t border-border-subtle')}>
                      <p className="px-4 pt-3 pb-1 eyebrow text-[10px] flex items-center gap-1.5">
                        <Search className="h-3 w-3" /> Annonces correspondantes
                      </p>
                      {suggestions.map((prop: any, i: number) => {
                        const idx = cities.length + i;
                        const focused = focusedIdx === idx;
                        return (
                          <button
                            key={prop.id}
                            type="button"
                            onMouseDown={() => { setActiveField(null); startTransition(() => router.push(`/annonce/${prop.slug}`)); }}
                            onMouseEnter={() => setFocusedIdx(idx)}
                            role="option"
                            aria-selected={focused}
                            className={cn(
                              'w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors',
                              focused ? 'bg-accent/[0.08]' : 'hover:bg-surface-cream',
                            )}
                          >
                            <div className={cn('w-8 h-8 rounded-lg flex items-center justify-center shrink-0', focused ? 'bg-accent/20 text-accent' : 'bg-primary/10 text-primary')}>
                              {PROP_TYPE_ICONS[prop.type] ?? <Home className="h-3.5 w-3.5" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-ink truncate"><Highlight text={prop.title} query={q} /></p>
                              <p className="text-xs text-ink-muted mt-0.5 flex items-center gap-1"><MapPin className="h-3 w-3 shrink-0" />{prop.city?.name || 'Maroc'}</p>
                            </div>
                            <p className="text-sm font-medium text-ink shrink-0">{formatPrice(Number(prop.price))}</p>
                          </button>
                        );
                      })}
                    </div>
                  )}

                  {!loadingSugg && cities.length === 0 && suggestions.length === 0 && (
                    <div className="px-4 py-5 text-center">
                      <p className="text-sm text-ink-muted">Aucun résultat pour «&nbsp;{q}&nbsp;»</p>
                    </div>
                  )}

                  {(cities.length > 0 || suggestions.length > 0) && (
                    <button
                      type="button"
                      onMouseDown={() => applyFilters()}
                      className="w-full flex items-center justify-center gap-2 px-4 py-3 text-xs font-medium text-accent border-t border-border-subtle hover:bg-accent/5 transition-colors"
                    >
                      <Search className="h-3.5 w-3.5" />
                      Voir tous les résultats pour «&nbsp;{q}&nbsp;»
                    </button>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <div className="h-5 w-px bg-border-subtle shrink-0" aria-hidden />

          {/* ── Type picker — two-column flyout ── */}
          <TypePickerDropdown
            open={isOpen('type')}
            onToggle={() => setActiveField(isOpen('type') ? null : 'type')}
            value={typeLabel}
            active={!!type}
            selected={type}
            onSelect={(v) => { setType(v); setActiveField(null); }}
            groups={groups}
            size="sm"
          />

          <div className="h-5 w-px bg-border-subtle shrink-0" aria-hidden />

          {/* ── Surface ── */}
          <RangeDropdown
            open={isOpen('surface')}
            onToggle={() => setActiveField(isOpen('surface') ? null : 'surface')}
            icon={<Maximize2 className="h-4 w-4" />}
            label="Superficie"
            value={surfaceLabel}
            active={!!(minSurface || maxSurface)}
            minValue={minSurface}
            maxValue={maxSurface}
            onMinChange={setMinSurface}
            onMaxChange={setMaxSurface}
            unit="m²"
            onClear={() => { setMinSurface(''); setMaxSurface(''); }}
            size="sm"
          />

          <div className="h-5 w-px bg-border-subtle shrink-0" aria-hidden />

          {/* ── Budget ── */}
          <RangeDropdown
            open={isOpen('budget')}
            onToggle={() => setActiveField(isOpen('budget') ? null : 'budget')}
            icon={<Banknote className="h-4 w-4" />}
            label={transactionType === 'rent' ? 'Loyer' : 'Budget'}
            value={budgetLabel}
            active={!!(minPrice || maxPrice)}
            minValue={minPrice}
            maxValue={maxPrice}
            onMinChange={setMinPrice}
            onMaxChange={setMaxPrice}
            unit="MAD"
            onClear={() => { setMinPrice(''); setMaxPrice(''); }}
            align="right"
            size="sm"
          />

          <div className="flex-1 min-w-2" />

          {/* ── Search ── */}
          <Button
            variant="primary"
            size="sm"
            onClick={applyFilters}
            loading={isPending}
            className="shrink-0"
          >
            {!isPending && <Search className="h-4 w-4" />}
            Rechercher
          </Button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// TypePickerDropdown
// ─────────────────────────────────────────────────────────────────────

function TypePickerDropdown({
  open, onToggle, value, active, selected, onSelect, groups, size = 'md',
}: {
  open: boolean;
  onToggle: () => void;
  value: string;
  active: boolean;
  selected: string;
  onSelect: (v: string) => void;
  groups: Array<{ group_key: string; group_label: string; values: Array<{ key: string; label: string }> }>;
  size?: 'sm' | 'md';
}) {
  const activeGroupKey =
    groups.find((g) => g.values.some((v) => v.key === selected))?.group_key ??
    groups[0]?.group_key ?? null;
  const [hoveredGroup, setHoveredGroup] = useState<string | null>(null);

  useEffect(() => {
    if (open) setHoveredGroup(selected ? activeGroupKey : null);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const currentGroup = groups.find((g) => g.group_key === hoveredGroup);
  const triggerWidthClass = size === 'sm' ? 'w-60' : 'w-52';

  return (
    <div className={cn('relative', triggerWidthClass)}>
      <button
        type="button"
        onClick={onToggle}
        className={cn(
          'w-full flex items-center gap-2 h-10 px-3 rounded-lg border bg-surface text-sm transition-colors',
          open || active ? 'border-cta ring-2 ring-cta/20' : 'border-border hover:border-ink-secondary',
        )}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <Building2 className={cn('h-4 w-4 shrink-0', open || active ? 'text-accent' : 'text-ink-muted')} />
        <span className={cn('flex-1 text-left truncate', active ? 'text-ink' : 'text-ink-muted')}>{value}</span>
        {active ? (
          <button
            type="button"
            onMouseDown={(e) => { e.stopPropagation(); onSelect(''); }}
            className="p-0.5 rounded-full hover:bg-surface-cream text-ink-muted hover:text-ink transition-colors shrink-0"
            aria-label="Effacer"
          >
            <X className="h-3 w-3" />
          </button>
        ) : (
          <ChevronDown className={cn('h-3.5 w-3.5 text-ink-muted shrink-0 transition-transform duration-200', open && 'rotate-180')} />
        )}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.15, ease: EASE_PREMIUM }}
            className={cn(
              'absolute left-0 top-full mt-1.5 flex bg-surface rounded-xl shadow-card-hover border border-border-subtle z-50 overflow-hidden',
              size === 'sm' && 'min-w-[22rem]',
            )}
            style={size === 'sm' ? undefined : { minWidth: '20rem' }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Left — groups */}
            <div className={cn(size === 'sm' ? 'w-44' : 'w-44', 'shrink-0 border-r border-border-subtle py-1.5')}>
              <button
                type="button"
                onMouseDown={(e) => { e.preventDefault(); onSelect(''); }}
                className={cn(
                  'w-full text-left px-3.5 py-2 text-sm transition-colors',
                  !selected ? 'bg-accent/10 text-ink font-medium' : 'text-ink-secondary hover:bg-surface-cream hover:text-ink',
                )}
              >
                Tous les types
              </button>
              <div className="my-1 border-t border-border-subtle" />
              {groups.map((g) => {
                const isHovered = hoveredGroup === g.group_key;
                const hasSelection = g.values.some((v) => v.key === selected);
                return (
                  <button
                    key={g.group_key}
                    type="button"
                    onMouseEnter={() => setHoveredGroup(g.group_key)}
                    onFocus={() => setHoveredGroup(g.group_key)}
                    className={cn(
                      'w-full flex items-center justify-between px-3.5 py-2.5 text-sm transition-colors',
                      isHovered ? 'bg-surface-cream text-ink'
                        : hasSelection ? 'text-accent font-medium hover:bg-surface-cream'
                          : 'text-ink-secondary hover:bg-surface-cream hover:text-ink',
                    )}
                  >
                    <span className={cn('font-medium leading-snug text-left', hasSelection && !isHovered && 'text-accent')}>
                      {g.group_label}
                    </span>
                    <ChevronDown className="h-3 w-3 -rotate-90 text-ink-muted shrink-0 ml-1" />
                  </button>
                );
              })}
            </div>
            {/* Right — values */}
            <div className="flex-1 py-1.5 min-w-[10rem]">
              {currentGroup ? (
                <>
                  <p className="px-4 pt-1 pb-1 text-[10px] uppercase font-semibold tracking-widest text-ink-muted">{currentGroup.group_label}</p>
                  {currentGroup.values.map((v) => {
                    const isSel = selected === v.key;
                    return (
                      <button
                        key={v.key}
                        type="button"
                        onMouseDown={(e) => { e.preventDefault(); onSelect(v.key); }}
                        className={cn(
                          'w-full text-left px-4 py-2 text-sm transition-colors flex items-center gap-2',
                          isSel ? 'bg-accent/10 text-ink font-medium' : 'text-ink-secondary hover:bg-surface-cream hover:text-ink',
                        )}
                      >
                        <span className={cn('w-1.5 h-1.5 rounded-full shrink-0', isSel ? 'bg-accent' : 'bg-transparent')} />
                        {v.label}
                      </button>
                    );
                  })}
                </>
              ) : (
                <p className="px-4 py-3 text-sm text-ink-muted">Survolez un groupe</p>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// RangeDropdown
// ─────────────────────────────────────────────────────────────────────

function RangeDropdown({
  open, onToggle, icon, label, value, active,
  minValue, maxValue, onMinChange, onMaxChange,
  unit, onClear, align = 'left', size = 'md',
}: {
  open: boolean;
  onToggle: () => void;
  icon: React.ReactNode;
  label: string;
  value: string;
  active: boolean;
  minValue: string;
  maxValue: string;
  onMinChange: (v: string) => void;
  onMaxChange: (v: string) => void;
  unit: string;
  onClear: () => void;
  align?: 'left' | 'right';
  size?: 'sm' | 'md';
}) {
  const triggerClassName = size === 'sm'
    ? 'w-full flex items-center gap-2 h-9 px-3 rounded-lg border bg-surface text-[13px] transition-colors'
    : 'w-full flex items-center gap-2 h-10 px-3 rounded-lg border bg-surface text-sm transition-colors';
  const panelClassName = size === 'sm'
    ? 'absolute top-full mt-1.5 w-96 bg-surface rounded-xl shadow-card-hover border border-border-subtle z-50 p-3.5'
    : 'absolute top-full mt-1.5 w-72 bg-surface rounded-xl shadow-card-hover border border-border-subtle z-50 p-4';
  const fieldClassName = size === 'sm' ? 'flex-none w-36' : 'flex-1';
  const inputClassName = size === 'sm'
    ? 'w-full h-9 px-3 pr-10 rounded-lg border border-border bg-surface-cream text-sm text-ink focus:outline-none focus:ring-2 focus:ring-accent/40 focus:border-accent tabular-nums'
    : 'w-full h-10 px-3 pr-10 rounded-lg border border-border bg-surface-cream text-sm text-ink focus:outline-none focus:ring-2 focus:ring-accent/40 focus:border-accent tabular-nums';
  const rootWidthClass = size === 'sm' ? 'w-60' : 'w-52';

  return (
    <div className={cn('relative', rootWidthClass)}>
      <button
        type="button"
        onClick={onToggle}
        className={cn(
          triggerClassName,
          open || active ? 'border-cta ring-2 ring-cta/20' : 'border-border hover:border-ink-secondary',
        )}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        <span className={cn('shrink-0', open || active ? 'text-accent' : 'text-ink-muted')}>{icon}</span>
        <span className={cn('flex-1 text-left truncate', active ? 'text-ink' : 'text-ink-muted')}>{value}</span>
        {active ? (
          <button
            type="button"
            onMouseDown={(e) => { e.stopPropagation(); onClear(); }}
            className="p-0.5 rounded-full hover:bg-surface-cream text-ink-muted hover:text-ink transition-colors shrink-0"
            aria-label="Effacer"
          >
            <X className="h-3 w-3" />
          </button>
        ) : (
          <ChevronDown className={cn('h-3.5 w-3.5 text-ink-muted shrink-0 transition-transform duration-200', open && 'rotate-180')} />
        )}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.15, ease: EASE_PREMIUM }}
            className={cn(
              panelClassName,
              align === 'right' ? 'right-0' : 'left-0',
            )}
            onClick={(e) => e.stopPropagation()}
          >
            <p className="text-xs font-semibold text-ink-muted uppercase tracking-wide mb-3">{label}</p>
            <div className={cn('flex items-end gap-3', size === 'sm' && 'gap-2.5')}>
              <div className={fieldClassName}>
                <label className="eyebrow text-[10px] mb-1.5 block">Min</label>
                <div className="relative">
                  <input
                    type="number"
                    value={minValue}
                    onChange={(e) => onMinChange(e.target.value)}
                    placeholder="Min"
                    min="0"
                    className={inputClassName}
                  />
                  <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[10px] text-ink-muted font-semibold pointer-events-none">{unit}</span>
                </div>
              </div>
              <span className={cn('text-ink-muted pb-2.5', size === 'sm' && 'pb-2')}>–</span>
              <div className={fieldClassName}>
                <label className="eyebrow text-[10px] mb-1.5 block">Max</label>
                <div className="relative">
                  <input
                    type="number"
                    value={maxValue}
                    onChange={(e) => onMaxChange(e.target.value)}
                    placeholder="Max"
                    min="0"
                    className={inputClassName}
                  />
                  <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[10px] text-ink-muted font-semibold pointer-events-none">{unit}</span>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Highlight
// ─────────────────────────────────────────────────────────────────────

function Highlight({ text, query }: { text: string; query: string }) {
  if (!query || query.length < 2) return <>{text}</>;
  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) return <>{text}</>;
  return (
    <>
      {text.slice(0, idx)}
      <span className="font-semibold text-accent">{text.slice(idx, idx + query.length)}</span>
      {text.slice(idx + query.length)}
    </>
  );
}
